// The titles a player can type, loaded once from the static list
// (public/titles.json, built from the film list by `npm run titles`).
//
// One request per visit, shared by every Call Sheet screen: the file is about
// 580KB compressed, and the browser's cache keeps it after the first time.
import { useEffect, useState } from 'react';
import type { TitleRecord } from './titles.ts';

// [kind, id, title, year, endYear, well known, extra keys?] -- see
// scripts/build-title-list.ts.
type Packed = ['m' | 't', number, string, number | null, number | null, 0 | 1, string[]?];

let loading: Promise<TitleRecord[]> | null = null;

export function loadTitles(): Promise<TitleRecord[]> {
  loading ??= fetch('/titles.json')
    .then((r) => {
      if (!r.ok) throw new Error(`title list: ${r.status}`);
      return r.json() as Promise<Packed[]>;
    })
    // The file is in fame order, most-voted first, and `rank` keeps it.
    .then((rows) => rows.map(([k, id, title, year, , known, alt], rank) => ({
      id, kind: k === 't' ? 'tv' as const : 'movie' as const, title, year, rank,
      ...(known === 1 ? { known: true } : {}), ...(alt ? { alt } : {})
    })))
    .catch((err: unknown) => { loading = null; throw err; });
  return loading;
}

/** The list, or null while it loads (or if it could not be fetched). */
export function useTitles(): TitleRecord[] | null {
  const [list, setList] = useState<TitleRecord[] | null>(null);
  useEffect(() => {
    let live = true;
    loadTitles().then((l) => { if (live) setList(l); }, () => {});
    return () => { live = false; };
  }, []);
  return list;
}
